import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { renderInlineMarkdown } from '../../utils/renderMarkdown';
import QuotaDisplay from '../upgrade/QuotaDisplay';
import AtLimitMessage from '../upgrade/AtLimitMessage';

// ── AI Ask Section ───────────────────────────────────────────────────────

function AskQuestion() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quota, setQuota] = useState(null);
  const tier = useEffectiveTier();
  const [prevTier, setPrevTier] = useState(tier);

  // Reset quota when tier changes, then fetch fresh quota
  if (tier !== prevTier) {
    setPrevTier(tier);
    setQuota(null);
  }

  // Fetch current quota proactively on mount and when tier changes
  useEffect(() => {
    betClient.get('/api/ai/usage?feature=education_qa')
      .then((r) => { if (r.data.quota) setQuota(r.data.quota); })
      .catch((e) => console.warn('[QuotaDisplay mount]', e?.response?.status, e?.message));
  }, [tier]);

  function ask() {
    if (!question.trim()) return;
    setLoading(true); setError(null); setAnswer(null);
    betClient.get(`/api/ai/betting-education?question=${encodeURIComponent(question.trim())}`)
      .then((r) => {
        if (r.data.quota) setQuota(r.data.quota);
        setAnswer(r.data);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 429 && detail?.quota) {
          setQuota(detail.quota);
        } else {
          setError(e.message);
        }
      })
      .finally(() => setLoading(false));
  }

  const atLimit = quota && !quota.unlimited && quota.remaining === 0;

  return (
    <div className="rounded-lg border-l-[3px] border-l-[#005BB3] dark:border-l-[#3b82f6] border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0f1219] px-5 py-4 mb-6">
      <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>Ask a Question</h3>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>Type any sports betting question and get a plain-English explanation from AI.</p>

      {atLimit ? (
        <AtLimitMessage featureLabel="Education Q&A" total={quota.total} featureKey="education_qa" />
      ) : (
        <>
          <div className="flex gap-2">
            <input
              type="text" value={question} onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
              placeholder="e.g. What does -110 mean? How do parlays work?"
              className="flex-1 px-3 py-2 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}
            />
            <button onClick={ask} disabled={loading || !question.trim()}
              className="px-4 py-2 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
              {loading ? 'Thinking...' : 'Ask'}
            </button>
          </div>
          <div className="mt-2">
            <QuotaDisplay quota={quota} feature="education_qa" suggestedTier="pro" />
          </div>
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 mt-3">
          <svg className="animate-spin h-3.5 w-3.5 text-[#005BB3]" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-[#6B7280] dark:text-[#8892a8] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>Generating...</span>
        </div>
      )}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] px-3 py-2 mt-3">
          <p className="text-[#DC2626] text-[13px]">Failed to get answer.</p>
          <button onClick={ask} className="text-[12px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {answer && !loading && (
        <div className="mt-3 pt-3 border-t border-[#E5E7EB] dark:border-[#1e2536]">
          {(answer.content || '').split('\n\n').map((para, i) => (
            <p key={i} className="text-[14px] leading-[1.7] text-[#1F2937] dark:text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{renderInlineMarkdown(para)}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Collapsible Topic ────────────────────────────────────────────────────

function Topic({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-[#E5E7EB] dark:border-[#1e2536] rounded-lg mb-1.5 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left bg-white dark:bg-[#151a25] hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150"
      >
        <span className="text-[14px] font-medium text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{title}</span>
        <svg className={`w-3.5 h-3.5 text-[#6B7280] dark:text-[#8892a8] transition-transform duration-150 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25]">
          {children}
        </div>
      )}
    </div>
  );
}

function P({ children }) {
  return <p className="text-[14px] leading-[1.7] text-[#1F2937] dark:text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{children}</p>;
}

function Example({ children }) {
  return (
    <div className="rounded-md bg-[#F8F9FA] dark:bg-[#0f1219] border border-[#E5E7EB] dark:border-[#1e2536] px-3 py-2 my-2">
      <p className="text-[13px] text-[#1F2937] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>{children}</p>
    </div>
  );
}

function AppLink({ to, children }) {
  return <Link to={to} className="text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] underline transition-colors duration-150">{children}</Link>;
}

// ── Section Header ───────────────────────────────────────────────────────

function SectionHeader({ label, description }) {
  return (
    <div className="mb-3 mt-6 first:mt-0">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
        <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>{label}</h3>
      </div>
      {description && <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] ml-[11px]" style={{ fontFamily: 'var(--font-sans)' }}>{description}</p>}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────

export default function LearnPage() {
  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Learn</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Betting Strategy Explainer</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Learn how sports betting works, from the basics to advanced strategies.</p>

      <div className="max-w-[800px]">
        <AskQuestion />

        {/* ── Basics ──────────────────────────────────────────────────────── */}

        <SectionHeader label="Basics" description="Start here if you're new to sports betting." />

        <Topic title="What is a moneyline?" defaultOpen>
          <P>A moneyline bet is the simplest type of wager: you pick which team will win the game outright. No point spreads, no handicaps -- just pick the winner.</P>
          <P>The odds tell you how much you'd win relative to your bet. A favorite has negative odds, and an underdog has positive odds.</P>
          <Example>Lakers -150: Bet $150 to win $100 profit. Chiefs +200: Bet $100 to win $200 profit.</Example>
          <P>The bigger the negative number, the heavier the favorite. The bigger the positive number, the bigger the underdog.</P>
        </Topic>

        <Topic title="How spreads (point handicaps) work">
          <P>The spread levels the playing field between a favorite and an underdog. The favorite must win by more than the spread for a spread bet to pay out.</P>
          <Example>Lakers -5.5 vs Celtics +5.5: If the Lakers win 110-103 (by 7), they "covered" the -5.5 spread. If they win 108-104 (by 4), the Celtics +5.5 wins.</Example>
          <P>The half-point (.5) prevents ties (pushes), ensuring every bet has a clear winner.</P>
        </Topic>

        <Topic title="Over/under (totals) explained">
          <P>A totals bet is on the combined score of both teams. The bookmaker sets a number, and you bet whether the actual total will be over or under that line.</P>
          <Example>Chiefs vs Bills, Total 47.5: Final score Chiefs 31, Bills 24 = 55 total. The Over wins. If it were 24-20 = 44, the Under wins.</Example>
          <P>Weather, pace of play, and injuries all affect totals. A rainy NFL game often goes under, while two fast-paced NBA teams tend to go over.</P>
        </Topic>

        <Topic title="How to read American odds">
          <P>American odds use + and - signs. <strong>Negative (-)</strong> means favorite: the number is how much you bet to win $100. <strong>Positive (+)</strong> means underdog: the number is how much you win on a $100 bet.</P>
          <Example>-110 (standard vig): Bet $110 to win $100. +150: Bet $100 to win $150. -300: Bet $300 to win $100 (heavy favorite). +500: Bet $100 to win $500 (big underdog).</Example>
          <P>The -110 line is the most common -- it's the bookmaker's standard price, with their cut (called the "vig" or "juice") built in.</P>
        </Topic>

        <Topic title="Implied probability">
          <P>Every set of odds implies a probability of that outcome happening. Converting odds to probability helps you assess whether a bet has value.</P>
          <Example>-150 = 150 / (150+100) = 60.0% implied probability. +200 = 100 / (200+100) = 33.3% implied probability.</Example>
          <P>If you think the Lakers have a 70% chance of winning but the odds imply only 60%, that's a value bet -- the line is mispriced in your favor. <AppLink to="/patterns">See moneyline performance vs implied probability</AppLink>.</P>
        </Topic>

        <Topic title="What 'covering the spread' means">
          <P>A team "covers the spread" when they beat the point handicap. For favorites, this means winning by more than the spread. For underdogs, it means losing by less than the spread (or winning outright).</P>
          <Example>Patriots -3.5: They must win by 4+ to cover. Eagles +7: They can lose by up to 6 and still cover. A 24-21 Eagles loss covers +7 but doesn't cover +3.</Example>
          <P>ATS (Against The Spread) records track how often a team covers. <AppLink to="/patterns">Check team ATS records in Pattern Finder</AppLink>.</P>
        </Topic>

        {/* ── Intermediate ────────────────────────────────────────────────── */}

        <SectionHeader label="Intermediate" description="Understand what moves lines and where value hides." />

        <Topic title="ATS records and why they matter">
          <P>A team's ATS record shows how often they cover the spread, not just win games. A team can be 10-2 straight up but 5-7 ATS if they keep winning by less than the spread.</P>
          <P>ATS records are more useful than win-loss records for spread bettors because they account for the point handicap that bookmakers set.</P>
          <Example>The Chiefs are 12-5 overall but 8-9 ATS. They win a lot, but the spreads are often too large for them to cover. Betting them ATS has been unprofitable.</Example>
          <P><AppLink to="/patterns">Explore team ATS records, home/away splits, and recent trends</AppLink>.</P>
        </Topic>

        <Topic title="Line movement and what causes it">
          <P>Lines move when bookmakers adjust the odds in response to betting action, new information, or sharp bettors. Tracking which direction a line moves -- and why -- is crucial for finding value.</P>
          <P>Common causes: injury reports, weather changes, public betting volume, and sharp money (professional bettors placing large wagers).</P>
          <Example>A line opens at Chiefs -3 and moves to Chiefs -4.5. This likely means sharps or heavy public action on the Chiefs, so the book moved the line to attract bets on the other side.</Example>
        </Topic>

        <Topic title="Closing line value (CLV)">
          <P>The closing line is the final line before the game starts. It's considered the most accurate reflection of true probability because it incorporates all available information and betting action.</P>
          <P>If you consistently get better numbers than the closing line, you're likely a long-term winner. This is called "beating the close."</P>
          <Example>You bet Bills +3.5 on Monday. By kickoff, the line has moved to Bills +2.5. You got an extra point of value -- that's positive CLV.</Example>
        </Topic>

        <Topic title="Sharp money vs public money">
          <P><strong>Public money</strong> comes from recreational bettors who tend to bet favorites, overs, and popular teams. <strong>Sharp money</strong> comes from professional bettors who wager based on models and analysis.</P>
          <P>When the line moves against public sentiment, it often signals sharp action. For example, if 80% of bets are on the favorite but the line moves toward the underdog, sharps are likely on the underdog side.</P>
          <P><AppLink to="/alerts">Check the Alerts page for steam moves and reverse line movement</AppLink>.</P>
        </Topic>

        <Topic title="How to spot value in odds">
          <P>Value exists when the implied probability of the odds is lower than the actual probability of the outcome. In other words, the bookmaker is offering you better odds than the event warrants.</P>
          <P>Compare lines across multiple bookmakers. If four books have a team at -150 but one has them at -130, the -130 line likely has value.</P>
          <Example>Consensus line: Bucks -6.5. BetMGM has Bucks -5.5. That extra point at -5.5 is value -- same team, better number.</Example>
        </Topic>

        <Topic title="Steam moves and reverse line movement">
          <P>A <strong>steam move</strong> happens when multiple bookmakers move the same line in the same direction simultaneously, usually triggered by a large sharp bet at one book that cascades across the market.</P>
          <P><strong>Reverse line movement (RLM)</strong> is when the line moves opposite to where the majority of bets are placed. If 75% of bets are on Team A but the line moves toward Team B, that's RLM -- a strong signal of sharp money on Team B.</P>
          <P><AppLink to="/alerts">The Alerts page flags steam moves and RLM in real time</AppLink>.</P>
        </Topic>

        {/* ── Advanced ────────────────────────────────────────────────────── */}

        <SectionHeader label="Advanced" description="Long-term strategies for serious bettors." />

        <Topic title="Bankroll management and unit sizing">
          <P>A "unit" is a fixed percentage of your bankroll -- typically 1-3%. Betting consistent units protects you from variance and prevents emotional overreaction after wins or losses.</P>
          <Example>Bankroll: $1,000. 1 unit = $10 (1%). A strong pick might be 2 units ($20). Never bet 10+ units on a single game -- that's how bankrolls get wiped out.</Example>
          <P>The Kelly Criterion is a more advanced approach that sizes bets based on your edge and the odds offered, but most bettors use a simplified version (quarter or half Kelly) to reduce volatility.</P>
        </Topic>

        <Topic title="Expected value (EV)">
          <P>EV is the average profit or loss per bet if you made the same wager thousands of times. Positive EV (+EV) bets are profitable long-term, even if individual bets lose.</P>
          <Example>You bet on a coin flip at +110 odds. EV = (50% x $110) - (50% x $100) = $55 - $50 = +$5. Each bet has +$5 EV, so you should take this bet every time.</Example>
          <P>The goal isn't to win every bet -- it's to consistently find +EV spots. A 55% win rate on -110 lines is solidly profitable over hundreds of bets.</P>
        </Topic>

        <Topic title="When to fade the public">
          <P>Fading the public means betting against popular opinion. This works because public money tends to inflate lines on favorites and overs, creating value on the other side.</P>
          <P>The best spots to fade the public are primetime games (everyone watches and bets favorites), large spreads (public loves to pile on blowout favorites), and popular teams regardless of matchup.</P>
          <Example>Monday Night Football: Cowboys -7, with 80% of bets on Dallas. The line hasn't moved despite lopsided action. The book is comfortable taking Cowboys money -- consider the opponent +7.</Example>
        </Topic>

        <Topic title="Line shopping across bookmakers">
          <P>Different bookmakers offer slightly different lines. Shopping for the best available number is the single easiest way to improve your long-term results -- it costs nothing and adds up significantly.</P>
          <Example>DraftKings: Bills -3.5 (-110). FanDuel: Bills -3 (-115). BetMGM: Bills -3.5 (-105). The FanDuel -3 line is the best number -- a half-point matters. The BetMGM -105 price is the best if you want -3.5.</Example>
          <P><AppLink to="/patterns">The Bookmaker Analysis in Pattern Finder ranks which books offer the best value</AppLink>.</P>
        </Topic>

        <Topic title="Using historical patterns for decisions">
          <P>Historical data helps you identify teams that consistently beat or miss expectations. A team that's 15-5 ATS at home is worth noting. A team that goes Over 70% of the time tells you about their game pace.</P>
          <P>But beware: small sample sizes are misleading. A 4-0 ATS streak doesn't predict the future. Look for patterns across 30+ games for meaningful trends, and always consider why the pattern exists (coaching style, roster, schedule).</P>
          <P><AppLink to="/patterns">Explore all historical patterns in the Pattern Finder</AppLink>.</P>
        </Topic>

        <Topic title="Common mistakes to avoid">
          <P><strong>Chasing losses:</strong> Doubling down after a loss to "get even" leads to spiraling bankroll depletion. Stick to your unit size.</P>
          <P><strong>Ignoring closing line value:</strong> If you consistently bet numbers that are worse than the closing line, you're likely a long-term loser regardless of recent results.</P>
          <P><strong>Overvaluing recent results:</strong> A team's last 3 games don't override a full season of data. Recency bias is the most common mistake in sports betting.</P>
          <P><strong>Not shopping lines:</strong> Taking -7 when -6.5 is available elsewhere is giving away free money. Always check multiple books before placing a bet.</P>
        </Topic>
      </div>
    </div>
  );
}
